import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Badge,
  Typography,
  Container,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Divider,
  Tooltip,
  Avatar,
  InputBase,
  Paper
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';

// Icons
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import MessageIcon from '@mui/icons-material/Message';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AppsIcon from '@mui/icons-material/Apps';
import BuildIcon from '@mui/icons-material/Build';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HelpIcon from '@mui/icons-material/Help';
import MemoryIcon from '@mui/icons-material/Memory';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Logo
import logo0 from '../assets/logo0.png';

// Styled components
const NavbarContainer = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#000000',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  position: 'sticky',
  top: 0,
  zIndex: 1100,
  width: '100%',
}));

const NavToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0 16px',
  minHeight: '52px',
  maxWidth: '1128px',
  width: '100%',
  margin: '0 auto',
  [theme.breakpoints.up('xl')]: {
    maxWidth: '1200px',
  },
}));

const LogoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const Logo = styled('img')({
  height: '34px',
  marginRight: '12px',
  filter: 'brightness(0) invert(1)', // Makes the logo white
});

const SearchContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha('#ffffff', 0.15),
  '&:hover': {
    backgroundColor: alpha('#ffffff', 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  maxWidth: '280px',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'rgba(255, 255, 255, 0.7)',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'white',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

const NavSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const NavItem = styled(Box)(({ theme, active }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0 12px',
  color: active ? '#FFE302' : '#FFFFFF',
  cursor: 'pointer',
  minWidth: '80px',
  height: '52px',
  justifyContent: 'center',
  borderBottom: active ? '2px solid #FFE302' : 'none',
  '&:hover': {
    color: '#FFE302',
  },
  [theme.breakpoints.down('md')]: {
    minWidth: 'auto',
    padding: '0 8px',
  },
}));

const NavText = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  marginTop: '4px',
  fontWeight: 400,
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  marginRight: theme.spacing(1),
  color: 'white',
  [theme.breakpoints.down('md')]: {
    display: 'flex',
  },
}));

const BadgeStyled = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#FFE302',
    color: '#000000',
    fontWeight: 'bold',
  },
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  backgroundColor: '#000000',
  color: 'white',
}));

const DrawerContent = styled(Box)({
  width: 280,
  backgroundColor: '#121212',
  color: 'white',
  height: '100%',
});

const DrawerNavItem = styled(ListItem)({
  padding: '12px 16px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
});

const Nav = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [appsAnchorEl, setAppsAnchorEl] = useState(null);
  
  const isActive = (path) => location.pathname === path;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleAppsClick = (event) => {
    setAppsAnchorEl(event.currentTarget);
  };

  const handleAppsClose = () => {
    setAppsAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    handleProfileClose();
    handleAppsClose();
    navigate(path);
  };

  // Main navigation items
  const navItems = [
    { name: 'Home', icon: <HomeIcon />, path: '/' },
    { name: 'Network', icon: <PeopleIcon />, path: '/network' },
    { name: 'Parts', icon: <MemoryIcon />, path: '/parts' },
    { name: 'Messaging', icon: <MessageIcon />, path: '/messenger', badge: 3 },
    { name: 'Notifications', icon: <NotificationsIcon />, path: '/notifications', badge: 5 },
    { name: 'Me', icon: <AccountCircleIcon />, path: '/profile', dropdown: true }
  ];

  // App drawer items
  const appItems = [
    { name: 'AI Diagnostics', icon: <SmartToyIcon />, path: '/ai-diagnostics' },
    { name: 'Parts Marketplace', icon: <ShoppingCartIcon />, path: '/parts' },
    { name: 'Repair Guides', icon: <BuildIcon />, path: '/repair-guides' },
    { name: 'Support', icon: <SupportAgentIcon />, path: '/support' },
    { name: 'Help Center', icon: <HelpIcon />, path: '/help' }
  ];

  // Profile menu items
  const profileMenuItems = [
    { name: 'Profile', icon: <AccountCircleIcon />, path: '/profile' },
    { name: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    { name: 'Logout', icon: <LogoutIcon />, path: '/logout' }
  ];

  return (
    <>
      <NavbarContainer>
        <NavToolbar>
          {/* Left section: Logo and Search */}
          <LogoContainer>
            <MobileMenuButton onClick={handleDrawerToggle}>
              <MenuIcon />
            </MobileMenuButton>
            
            <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
              <Logo src={logo0} alt="Warrili Logo" />
            </Link>
            
            {!isMobile && (
              <SearchContainer>
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <StyledInputBase
                  placeholder="Search…"
                  inputProps={{ 'aria-label': 'search' }}
                />
              </SearchContainer>
            )}
          </LogoContainer>

          {/* Center section: Main navigation */}
          {!isMobile && (
            <NavSection sx={{ flexGrow: 0, justifyContent: 'center', ml: 'auto', mr: 'auto' }}>
              {navItems.map((item) => (
                item.dropdown ? (
                  <Box key={item.name}>
                    <NavItem 
                      active={isActive(item.path)}
                      onClick={handleProfileClick}
                      sx={{ cursor: 'pointer' }}
                    >
                      {item.badge ? (
                        <BadgeStyled badgeContent={item.badge} color="secondary">
                          {item.icon}
                        </BadgeStyled>
                      ) : (
                        item.icon
                      )}
                      <NavText>{item.name}</NavText>
                      <ArrowDropDownIcon fontSize="small" />
                    </NavItem>
                    <Menu
                      anchorEl={profileAnchorEl}
                      open={Boolean(profileAnchorEl)}
                      onClose={handleProfileClose}
                      PaperProps={{
                        sx: { backgroundColor: '#121212', color: 'white' }
                      }}
                    >
                      {profileMenuItems.map((menuItem) => (
                        <MenuItem 
                          key={menuItem.name} 
                          onClick={() => handleMenuItemClick(menuItem.path)}
                          sx={{ '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
                        >
                          <ListItemIcon sx={{ color: 'white', minWidth: '36px' }}>
                            {menuItem.icon}
                          </ListItemIcon>
                          <ListItemText>{menuItem.name}</ListItemText>
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                ) : (
                  <NavItem 
                    key={item.name} 
                    active={isActive(item.path)}
                    onClick={() => navigate(item.path)}
                  >
                    {item.badge ? (
                      <BadgeStyled badgeContent={item.badge} color="secondary">
                        {item.icon}
                      </BadgeStyled>
                    ) : (
                      item.icon
                    )}
                    <NavText>{item.name}</NavText>
                  </NavItem>
                )
              ))}
            </NavSection>
          )}

          {/* Right section: Apps and Work */}
          <NavSection sx={{ ml: 'auto' }}>
            {isMobile && (
              <IconButton color="inherit" sx={{ mr: 1 }}>
                <SearchIcon />
              </IconButton>
            )}
            
            <Box>
              <NavItem onClick={handleAppsClick}>
                <AppsIcon />
                <NavText>Apps</NavText>
              </NavItem>
              <Menu
                anchorEl={appsAnchorEl}
                open={Boolean(appsAnchorEl)}
                onClose={handleAppsClose}
                PaperProps={{
                  sx: { backgroundColor: '#121212', color: 'white' }
                }}
              >
                {appItems.map((appItem) => (
                  <MenuItem 
                    key={appItem.name} 
                    onClick={() => handleMenuItemClick(appItem.path)}
                    sx={{ '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
                  >
                    <ListItemIcon sx={{ color: 'white', minWidth: '36px' }}>
                      {appItem.icon}
                    </ListItemIcon>
                    <ListItemText>{appItem.name}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            
            {isMobile && (
              <IconButton 
                color="inherit" 
                onClick={handleProfileClick}
                sx={{ ml: 1 }}
              >
                <AccountCircleIcon />
              </IconButton>
            )}
          </NavSection>
        </NavToolbar>
      </NavbarContainer>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: 280, backgroundColor: '#121212', color: 'white' } }}
      >
        <DrawerContent>
          <DrawerHeader>
            <Typography variant="h6">Warrili</Typography>
            <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </DrawerHeader>
          <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
          
          <List>
            {navItems.filter(item => !item.dropdown).map((item) => (
              <DrawerNavItem 
                key={item.name} 
                button 
                onClick={() => {
                  navigate(item.path);
                  handleDrawerToggle();
                }}
                selected={isActive(item.path)}
                sx={{
                  backgroundColor: isActive(item.path) ? 'rgba(255, 227, 2, 0.15)' : 'transparent',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 227, 2, 0.15)',
                  }
                }}
              >
                <ListItemIcon sx={{ color: isActive(item.path) ? '#FFE302' : 'white' }}>
                  {item.badge ? (
                    <BadgeStyled badgeContent={item.badge} color="secondary">
                      {item.icon}
                    </BadgeStyled>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary={item.name} 
                  sx={{ color: isActive(item.path) ? '#FFE302' : 'white' }}
                />
              </DrawerNavItem>
            ))}
          </List>
          
          <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', my: 1 }} />
          <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
            Apps
          </Typography>
          
          <List>
            {appItems.map((item) => (
              <DrawerNavItem 
                key={item.name} 
                button 
                onClick={() => {
                  navigate(item.path);
                  handleDrawerToggle();
                }}
                selected={isActive(item.path)}
                sx={{
                  backgroundColor: isActive(item.path) ? 'rgba(255, 227, 2, 0.15)' : 'transparent',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 227, 2, 0.15)',
                  }
                }}
              >
                <ListItemIcon sx={{ color: isActive(item.path) ? '#FFE302' : 'white' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.name} 
                  sx={{ color: isActive(item.path) ? '#FFE302' : 'white' }}
                />
              </DrawerNavItem>
            ))}
          </List>
          
          <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', my: 1 }} />
          <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
            Account
          </Typography>
          
          <List>
            {profileMenuItems.map((item) => (
              <DrawerNavItem 
                key={item.name} 
                button 
                onClick={() => {
                  navigate(item.path);
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon sx={{ color: 'white' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.name} />
              </DrawerNavItem>
            ))}
          </List>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default Nav;